import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {FmlxIcon, FmlxTooltip} from 'fmlx-common-ui';

import CardName from './CardName';

import {CardInvalidType, CardType, ExecutionStatus, RuntimeMode} from '../../../../constants/dataEnum';

import {mergeRefs} from '../../../../lib/refHelper';
import withIntl from '../../../../lib/withIntl';

import './Card.scss';
import TaskListCheckbox from '../Components/TaskListCheckbox';

const Card = React.forwardRef(
  (
    {
      ID,
      name,
      isSelected,
      onMouseDown,
      onMouseUp,
      onDelete,
      onCheckedAllChange,
      children,
      drop,
      drag,
      dragPreview,
      isOver,
      canDrop,
      disableDrag,
      runtimeMode,
      executionStatus,
      className,
      invalid,
      invalidTypes,
      isIgnored,
      isExpanded,
      onMouseDownExpand,
      onMouseUpExpand,
      intl,
      showSettingIcon,
      settingIconDisabled,
      onSettingClick,
      onCardNameChange,
      cardType,
      maxCharacters,
      runtimeStatus,
      onShowMenuContext,
      disableMenuContext,
      isViewMode,
      hideCheckbox,
      isNameReadOnly,
      requiredTips,
    },
    ref,
  ) => {
    const cardRef = useRef(null);
    const clickedRef = useRef(false);

    const generateInvalidMessage = () => {
      const invalidMessages = [];

      if (invalidTypes?.length > 0) {
        invalidTypes.forEach((invalidEle) => {
          switch (invalidEle.error) {
            case CardInvalidType.PICK_TIP:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.invalidPickTip'})}</li>,
              );
              break;
            case CardInvalidType.PICK_TIP_DOES_NOT_EXIST:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.invalidPickTipDoesNotExist'})}</li>,
              );
              break;
            case CardInvalidType.EJECT_TIP:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.invalidEjectTip'})}</li>,
              );
              break;
            case CardInvalidType.EJECT_TIP_DOES_NOT_EXIST:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.invalidEjectTipDoesNotExist'})}</li>,
              );
              break;
            case CardInvalidType.ASPIRATE:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.invalidAspirate'})}</li>,
              );
              break;
            case CardInvalidType.DISPENSE:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.invalidDispense'})}</li>,
              );
              break;
            case CardInvalidType.INSUFFICIENT_ASPIRATE_VOLUME:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.insufficientAspireteVolume'})}</li>,
              );
              break;
            case CardInvalidType.OVER_ASPIRATE:
              invalidMessages.push(<li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.overAspirate'})}</li>);
              break;
            case CardInvalidType.ZERO_ASPIRATE:
              invalidMessages.push(<li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.zeroAspirate'})}</li>);
              break;
            case CardInvalidType.AIR_GAP_ORDER:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.invalidAirGapOrder'})}</li>,
              );
              break;
            case CardInvalidType.AIR_GAP_VOLUME:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.invalidAirGapVolume'})}</li>,
              );
              break;
            case CardInvalidType.REDEFINE:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.redefineAspirateDispense'})}</li>,
              );
              break;
            case CardInvalidType.LABWARE_NOT_FOUND:
              invalidMessages.push(
                <li>{intl.formatMessage({id: 'protocolEditor.taskList.tooltip.labwareNotFound'})}</li>,
              );
              break;
            case CardInvalidType.LIQUID_REMAINS_IN_TIP_FOR_MIXING:
              invalidMessages.push(
                <li>
                  {intl.formatMessage(
                    {id: 'protocolEditor.taskList.tooltip.liquidRemainsInTipForMixing'},
                    {taskNumber: invalidEle.number},
                  )}
                </li>,
              );
              break;
            case CardInvalidType.MIXING_WITHOUT_PICK_TIP:
              invalidMessages.push(
                <li>
                  {intl.formatMessage(
                    {id: 'protocolEditor.taskList.tooltip.mixingWithoutPickTip'},
                    {taskNumber: invalidEle.number},
                  )}
                </li>,
              );
              break;
            default:
              break;
          }
        });
      }

      return (
        <ul
          style={{
            margin: 5,
            padding: 0,
            paddingLeft: 10,
          }}
        >
          {invalidMessages}
        </ul>
      );
    };

    const generateCardClassName = () => {
      // {`card  ${isSelected && 'selected'} ${runtimeMode && executionStatus} ${isDragging && 'dragging'} ${invalid && 'background-red'} ${className} background-gray`}
      const classNames = ['card-component'];

      if (isSelected) {
        classNames.push('selected');
      }

      if (runtimeMode) {
        classNames.push(executionStatus);
      }

      if (isOver && canDrop) {
        classNames.push('hovered');
      }

      if (invalid) {
        classNames.push('background-red');
      }

      if (!isSelected) {
        classNames.push('background-gray');
      }

      return `${classNames.join(' ')} ${className}`;
    };

    const onCardNameChangeHandler = (value) => {
      onCardNameChange(ID, name, value);
    };

    const onCheckedChanged = (e) => {
      onCheckedAllChange(ID, cardType, e.target.checked);
    };

    const mouseDownHandler = (e) => {
      if (e.button === 0) {
        // only listen to left button mouse click
        clickedRef.current = true;
        onMouseDown(e, ID, cardType);
      }
    };

    const mouseUpHandler = (e) => {
      e.stopPropagation();
      if (
        clickedRef.current &&
        e.button === 0 && // only listen to left button mouse click
        (!runtimeMode || (runtimeMode && runtimeStatus !== RuntimeMode.RUNNING))
      ) {
        clickedRef.current = false;
        onMouseUp(e, ID, cardType);
      }
    };

    const getCardTipCount = () => {
      if (cardType === CardType.CONSOLIDATE_TIPS) {
        return '';
      }
      if (invalidTypes?.length > 0) {
        return <span>N/A</span>;
      }
      return <span>{requiredTips} tips</span>;
    };

    // handle when click released outside the card
    useEffect(() => {
      document.addEventListener('mouseup', mouseUpHandler);
      return () => {
        document.removeEventListener('mouseup', mouseUpHandler);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div className={generateCardClassName()} ref={mergeRefs(drop, dragPreview, cardRef, ref)} role="presentation">
        <div
          ref={(el) => {
            // eslint-disable-next-line no-unused-expressions
            !disableDrag && drag && drag(el);
          }}
          className="card-component-header"
          onMouseDown={mouseDownHandler}
          onMouseUp={mouseUpHandler}
          onContextMenu={(e) => {
            if (!disableMenuContext) {
              onShowMenuContext(e);
            }
          }}
          role="presentation"
        >
          <div className="card-component-header-left">
            {!runtimeMode && !isViewMode && (
              <img
                className="close-button"
                src="./svg/protocol-editor/remove-task.svg"
                alt="Remove Task"
                role="presentation"
                onClick={() => onDelete(ID, cardType)}
              />
            )}
            <CardName
              className={`${isSelected ? 'bg-yellow' : ''}
              ${invalid ? 'bg-red' : ''}
              ${isSelected && (cardType === CardType.CONFIRMATION || cardType === CardType.DELAY) ? 'bg-blue' : ''}`}
              name={name}
              onCardNameChange={onCardNameChangeHandler}
              invalid={invalid}
              tooltip=""
              runtimeMode={runtimeMode}
              maxCharacters={maxCharacters}
              isReadOnly={isViewMode || isNameReadOnly}
            />
            {invalid && (
              <FmlxTooltip title={generateInvalidMessage()}>
                <span className="info-button">
                  <FmlxIcon name="Warning" size="xs" customColor="white" />
                </span>
              </FmlxTooltip>
            )}
          </div>
          {getCardTipCount()}
          <div className="card-component-header-right">
            {!runtimeMode && !isViewMode && !hideCheckbox && (
              <>
                {showSettingIcon && (
                  <div className={`setting ${settingIconDisabled ? 'disabled' : ''}`}>
                    <span
                      className={`icon-preview-serial-dilution ${settingIconDisabled ? ' disabled' : ''}`}
                      onClick={onSettingClick}
                      role="presentation"
                      style={{fontSize: '20px'}}
                    />
                  </div>
                )}
                <div onMouseDown={(e) => e.stopPropagation()} aria-hidden="true">
                  <TaskListCheckbox disabled={invalid} checked={!isIgnored} onChange={onCheckedChanged} />
                </div>
              </>
            )}
            {runtimeMode && executionStatus === ExecutionStatus.EXECUTED && (
              <img className="executing-status" src="./svg/protocol-editor/execution-done-white.svg" alt="Done" />
            )}
          </div>
        </div>
        <div className={`card-component-content ${!isExpanded && 'hidden'}`} role="presentation">
          {children}
        </div>
        <hr className="card-component-footer-sparator" />
        <div
          className="card-component-footer"
          onMouseDown={(e) => {
            e.stopPropagation();
            // eslint-disable-next-line no-unused-expressions
            (!runtimeMode || (runtimeMode && runtimeStatus !== RuntimeMode.RUNNING)) &&
              onMouseDownExpand(e, ID, !isExpanded);
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
            // eslint-disable-next-line no-unused-expressions
            (!runtimeMode || (runtimeMode && runtimeStatus !== RuntimeMode.RUNNING)) &&
              onMouseUpExpand(e, ID, !isExpanded);
          }}
          role="presentation"
        >
          <span className={`icon-chevron-${isExpanded && isSelected ? 'up' : 'down'}`} />
        </div>
      </div>
    );
  },
);

Card.propTypes = {
  ID: PropTypes.any.isRequired,
  name: PropTypes.string,
  onDelete: PropTypes.func,
  drop: PropTypes.any,
  drag: PropTypes.func,
  dragPreview: PropTypes.func,
  disableDrag: PropTypes.bool,
  onCheckedAllChange: PropTypes.func,
  isOver: PropTypes.bool,
  canDrop: PropTypes.bool,
  children: PropTypes.any,
  isSelected: PropTypes.bool,
  onMouseDown: PropTypes.func,
  onMouseUp: PropTypes.func,
  runtimeMode: PropTypes.bool,
  executionStatus: PropTypes.string,
  className: PropTypes.string,
  invalid: PropTypes.bool,
  invalidTypes: PropTypes.array,
  isIgnored: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onMouseDownExpand: PropTypes.func,
  onMouseUpExpand: PropTypes.func,
  intl: PropTypes.any,
  showSettingIcon: PropTypes.bool,
  settingIconDisabled: PropTypes.bool,
  onSettingClick: PropTypes.func,
  onCardNameChange: PropTypes.func,
  cardType: PropTypes.string,
  maxCharacters: PropTypes.number,
  runtimeStatus: PropTypes.string.isRequired,
  onShowMenuContext: PropTypes.func,
  disableMenuContext: PropTypes.bool,
  isViewMode: PropTypes.bool.isRequired,
  hideCheckbox: PropTypes.bool,
  isNameReadOnly: PropTypes.bool,
  requiredTips: PropTypes.number,
};

Card.defaultProps = {
  name: 'Simple Transfer',
  onDelete: (cardId) => console.log('Card onDelete', cardId),
  onCheckedAllChange: (ID, checked) => {
    console.log('onCheckedAllChange', checked, ID);
  },
  drop: null,
  drag: null,
  dragPreview: null,
  isOver: false,
  canDrop: false,
  disableDrag: true,
  children: null,
  isSelected: false,
  onMouseDown: () => {},
  onMouseUp: () => {},
  runtimeMode: false,
  executionStatus: '',
  className: '',
  invalid: false,
  invalidTypes: [],
  isIgnored: false,
  isExpanded: false,
  onMouseDownExpand: (cardId) => console.log('Card onMouseDownExpand', cardId),
  onMouseUpExpand: (cardId) => console.log('Card onMouseUpExpand', cardId),
  intl: null,
  showSettingIcon: false,
  settingIconDisabled: false,
  onSettingClick: () => {},
  onCardNameChange: () => {},
  cardType: '',
  maxCharacters: 25,
  onShowMenuContext: () => {},
  disableMenuContext: false,
  hideCheckbox: false,
  isNameReadOnly: false,
  requiredTips: 0,
};

export default withIntl(Card);
